import React, { useMemo, useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import GuesthouseAdminEditBooking from "./GuesthouseAdminEditBooking";
import GuesthouseStaffEditBooking from "./GuesthouseStaffEditBooking";
import GuesthouseVisitorEditBooking from "./GuesthouseVisitorEditBooking";

const API_ROOT = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
const GH_API = API_ROOT.endsWith("/api")
  ? `${API_ROOT}/guesthouse`
  : `${API_ROOT}/api/guesthouse`;

const toInputDate = (d) => d.toISOString().slice(0, 10);

const CELL_BG = {
  green:  "lightgreen",   // checked-in
  brown:  "burlywood",    // booked / pending check-in
  red:    "#ff4d4d",      // payment pending (RStatus P)
  blue:   "lightblue",    // checked-out
};

const fmtHeaderDate = (val) => {
  if (!val) return "";
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "2-digit" });
};

export default function GuesthouseRoomLogbook() {
  const navigate = useNavigate();

  const user = useMemo(() => {
    try {
      const raw = localStorage.getItem("user");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, []);

  const moduleId = parseInt(user?.moduleId, 10);

  const yesterday = useMemo(() => { const d = new Date(); d.setDate(d.getDate() - 1); return d; }, []);
  const sixLater  = useMemo(() => { const d = new Date(); d.setDate(d.getDate() + 6); return d; }, []);

  const [guestHouses,  setGuestHouses]  = useState([]);
  const [guestHouseID, setGuestHouseID] = useState("");
  const [fromDate,     setFromDate]     = useState(toInputDate(yesterday));
  const [toDate,       setToDate]       = useState(toInputDate(sixLater));
  const [rows,         setRows]         = useState([]);
  const [dates,        setDates]        = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState("");
  const [expandedRooms, setExpandedRooms] = useState(new Set());
  // editTarget: null | { bookingId, visitorId (null=primary), bookingRef (null=primary) }
  const [editTarget, setEditTarget] = useState(null);

  // Guard: only moduleId 10 (Guesthouse Admin) or 11/22/23/27 (Reception) may use this page
  useEffect(() => {
    if (![10, 11, 22, 23, 27, 28].includes(moduleId)) {
      navigate("/dashboard", { replace: true });
    }
  }, [moduleId, navigate]);

  // Fetch guest houses on mount
  useEffect(() => {
    const load = async () => {
      try {
        const res  = await fetch(`${GH_API}/options?moduleId=${moduleId}`);
        const data = await res.json();
        if (!res.ok) throw new Error(data.message || "Failed to load guest houses");
        if (Array.isArray(data) && data.length) {
          setGuestHouses(data);
          setGuestHouseID(String(data[0].GuestHouseID));
        }
      } catch (e) {
        setError(e.message);
      }
    };
    if ([10, 11, 22, 23, 27, 28].includes(moduleId)) load();
  }, [moduleId]);

  const fetchLogbook = async () => {
    setError("");
    if (!guestHouseID) { setError("Please select a Guest House."); return; }
    if (!fromDate || !toDate) { setError("From Date and To Date are required."); return; }
    try {
      setLoading(true);
      const q = new URLSearchParams({ moduleId, guestHouseID, fromDate, toDate });
      const res  = await fetch(`${GH_API}/room-logbook?${q}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to fetch logbook");
      setDates(Array.isArray(data?.dates) ? data.dates : []);
      setRows(Array.isArray(data?.rows) ? data.rows : []);
    } catch (e) {
      setDates([]);
      setRows([]);
      setError(e.message || "Failed to fetch logbook");
    } finally {
      setLoading(false);
    }
  };

  // Auto-load logbook with default dates when reception modules (11/22/23/27) first land here
  const autoFetchedRef = useRef(false);
  useEffect(() => {
    if (!guestHouseID || autoFetchedRef.current) return;
    if (![11, 22, 23, 27, 28].includes(moduleId)) return;
    autoFetchedRef.current = true;
    fetchLogbook();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guestHouseID]);

  const toggleRoomExpand = (roomId) => {
    setExpandedRooms(prev => {
      const next = new Set(prev);
      if (next.has(roomId)) next.delete(roomId);
      else next.add(roomId);
      return next;
    });
  };

  const openEdit = ({ bookingId, visitorId = null, bookingRef = null }) => {
    if (!bookingId) return;
    setEditTarget({ bookingId, visitorId, bookingRef });
  };

  return (
    <>
    <section className="container-fluid">
      <h3 className="ctitle mb-3">Room Logbook</h3>

      {/* Filter form */}
      <form onSubmit={(e) => { e.preventDefault(); fetchLogbook(); }} className="mb-3">
        <table style={{ borderCollapse: 'collapse', width: 'auto', border: '0' }}>
          <tbody>
            <tr style={{ border: '0' }}>
              <td style={{ border: '0', padding: '0 8px 4px 0' }}><label className="form-label fw-semibold mb-0">Guest House</label></td>
              <td style={{ border: '0', padding: '0 8px 4px 0' }}><label className="form-label fw-semibold mb-0">From Date</label></td>
              <td style={{ border: '0', padding: '0 8px 4px 0' }}><label className="form-label fw-semibold mb-0">To Date</label></td>
              <td style={{ border: '0', padding: '0 8px 4px 0' }}><label className="form-label fw-semibold mb-0">View</label></td>
            </tr>
            <tr style={{ border: '0' }}>
              <td style={{ border: '0', padding: '0 8px 0 0' }}>
                <select
                  className="form-select form-select-sm"
                  value={guestHouseID}
                  onChange={(e) => setGuestHouseID(e.target.value)}
                  required
                >
                  <option value="">Select Guest House</option>
                  {guestHouses.map((g) => (
                    <option key={g.GuestHouseID} value={g.GuestHouseID}>
                      {g.GuestHouse}
                    </option>
                  ))}
                </select>
              </td>
              <td style={{ border: '0', padding: '0 8px 0 0' }}>
                <input
                  className="form-control form-control-sm"
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  required
                />
              </td>
              <td style={{ border: '0', padding: '0 8px 0 0' }}>
                <input
                  className="form-control form-control-sm"
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  required
                />
              </td>
              <td style={{ border: '0', padding: '0', verticalAlign: 'bottom' }}>
                <button className="btn btn-success btn-sm" type="submit" disabled={loading} style={{ padding: '5px 24px' }}>
                  {loading ? "Loading…" : "View"}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </form>

      {error && <div className="alert alert-danger">{error}</div>}

      <div className="table-responsive">
        <table className="table table-bordered table-sm align-middle" style={{ whiteSpace: "nowrap" }}>
          <thead style={{ '--bs-table-bg': '#008080', color: 'white' }}>
            <tr>
              <th style={{ minWidth: '120px', whiteSpace: 'nowrap' }}>Room</th>
              {dates.map((d) => (
                <th key={d} style={{ padding: "3px" }}>{fmtHeaderDate(d)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={Math.max(2, dates.length + 1)} className="text-center text-muted py-3">
                  No records found.
                </td>
              </tr>
            )}
            {loading && (
              <tr>
                <td colSpan={Math.max(2, dates.length + 1)} className="text-center text-muted py-3">
                  Loading…
                </td>
              </tr>
            )}
            {rows.map((room) => {
              const isExpanded = expandedRooms.has(room.RoomID);
              return (
                <React.Fragment key={room.RoomID}>
                  <tr>
                    <td className="fw-semibold" style={{ whiteSpace: 'nowrap', minWidth: '120px' }}>
                      <button
                        type="button"
                        onClick={() => toggleRoomExpand(room.RoomID)}
                        aria-label={isExpanded ? "Collapse" : "Expand"}
                        title={isExpanded ? "Collapse" : "Expand to view all dates"}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '16px',
                          height: '16px',
                          fontSize: '12px',
                          lineHeight: 1,
                          border: '1px solid #999',
                          borderRadius: '2px',
                          background: '#f5f5f5',
                          cursor: 'pointer',
                          marginRight: '5px',
                          padding: 0,
                          verticalAlign: 'middle',
                          flexShrink: 0,
                        }}
                      >
                        {isExpanded ? '−' : '+'}
                      </button>
                      {room.Room}
                    </td>
                    {(room.cells || []).map((cell) => (
                      <td
                        key={`${room.RoomID}-${cell.date}`}
                        style={{
                          backgroundColor: CELL_BG[cell.color] || "",
                          cursor: cell.bookingID ? "pointer" : "default",
                          textDecoration: cell.bookingID ? "underline" : "none"
                        }}
                        onClick={cell.bookingID ? () => openEdit({ bookingId: cell.bookingID, visitorId: cell.visitorID != null ? cell.visitorID : null, bookingRef: cell.bookingRef || null }) : undefined}
                        title={cell.bookingID ? "Open Edit" : ""}
                      >
                        {cell.visitorName || ""}
                      </td>
                    ))}
                  </tr>
                  {isExpanded && (
                    <>
                      <tr className="table-light">
                        <td className="text-muted small text-end">Mobile</td>
                        {(room.cells || []).map((cell) => (
                          <td key={`${room.RoomID}-mobile-${cell.date}`} className="small" style={{ backgroundColor: CELL_BG[cell.color] || "" }}>
                            {cell.mobile || ""}
                          </td>
                        ))}
                      </tr>
                      <tr className="table-light">
                        <td className="text-muted small text-end">Booked By</td>
                        {(room.cells || []).map((cell) => (
                          <td key={`${room.RoomID}-bookedby-${cell.date}`} className="small" style={{ backgroundColor: CELL_BG[cell.color] || "" }}>
                            {cell.bookedBy || ""}
                          </td>
                        ))}
                      </tr>
                      <tr className="table-light">
                        <td className="text-muted small text-end">Booking ID</td>
                        {(room.cells || []).map((cell) => (
                          <td key={`${room.RoomID}-bookingid-${cell.date}`} className="small" style={{ backgroundColor: CELL_BG[cell.color] || "" }}>
                            {cell.bookingID ? `${cell.bookingID}${cell.bookingRef || ""}` : ""}
                          </td>
                        ))}
                      </tr>
                    </>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>


    </section>

    {editTarget && (
      [11, 22, 23, 27, 28].includes(moduleId) ? (
        <GuesthouseStaffEditBooking
          bookingId={editTarget.bookingId}
          moduleId={moduleId}
          onClose={() => setEditTarget(null)}
          onSaved={() => { setEditTarget(null); fetchLogbook(); }}
        />
      ) : moduleId === 10 && editTarget.visitorId != null ? (
        <GuesthouseVisitorEditBooking
          bookingId={editTarget.bookingId}
          visitorId={editTarget.visitorId}
          bookingRef={editTarget.bookingRef}
          moduleId={moduleId}
          onClose={() => setEditTarget(null)}
          onSaved={() => { setEditTarget(null); fetchLogbook(); }}
        />
      ) : (
        <GuesthouseAdminEditBooking
          bookingId={editTarget.bookingId}
          moduleId={moduleId}
          onClose={() => setEditTarget(null)}
          onSaved={() => { setEditTarget(null); fetchLogbook(); }}
        />
      )
    )}
    </>
  );
}
